/**
 * OptionChainPage.js
 *
 * Top-level page for the Option Chain feature.
 * Toolbar: underlying selector | expiry selector | Data/Greeks toggle | meta info
 * Body:    OptionChainTable (virtual scrolling)
 * Footer:  StrategyBuilderFooter (slide-up on selection)
 */
import React, { useState, useEffect } from 'react';
import {
    Box, Stack, Autocomplete, TextField, ToggleButton, ToggleButtonGroup,
    Chip, Tooltip, Typography, Skeleton, Alert, Divider, Button,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { alpha }             from '@mui/material/styles';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import useOptionChain, {
    useExpiries,
    useSubscriptionConfig,
    useUnderlyings,
    saveSubscriptionConfig,
    resetSubscriptionConfig,
} from './useOptionChain';
import OptionChainTable      from './OptionChainTable';
import StrategyBuilderFooter from './StrategyBuilderFooter';
import useOptionChainStore   from './useOptionChainStore';
import { computePCR, relativeTime, fmtLTP } from './optionChainUtils';

// ─── Toolbar ──────────────────────────────────────────────────────────────────
function Toolbar({
    underlying, onUnderlying,
    expiry, onExpiry,
    viewMode, onViewMode,
    underlyings, expiries,
    spot, stale, refreshedAt, pcr,
}) {
    return (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            flexWrap='wrap'
            sx={{ mb: 1.5 }}
        >
            {/* Underlying selector */}
            <Autocomplete
                options={underlyings ?? []}
                value={underlying}
                onChange={(_, v) => v && onUnderlying(v)}
                renderInput={(params) => (
                    <TextField {...params} label='Underlying' size='small' sx={{ minWidth: 150 }} />
                )}
                disableClearable
                size='small'
            />

            {/* Expiry selector */}
            <Autocomplete
                options={expiries ?? []}
                value={expiry}
                onChange={(_, v) => v && onExpiry(v)}
                renderInput={(params) => (
                    <TextField {...params} label='Expiry' size='small' sx={{ minWidth: 140 }} />
                )}
                disableClearable
                size='small'
            />

            {/* Data / Greeks toggle */}
            <ToggleButtonGroup
                size='small'
                exclusive
                value={viewMode}
                onChange={(_, v) => v && onViewMode(v)}
                sx={{ height: 36 }}
            >
                <ToggleButton value='data' sx={{ px: 2, fontSize: '0.75rem', fontWeight: 700 }}>
                    Data
                </ToggleButton>
                <ToggleButton value='greeks' sx={{ px: 2, fontSize: '0.75rem', fontWeight: 700 }}>
                    Greeks
                </ToggleButton>
            </ToggleButtonGroup>

            {/* Spacer */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Meta chips */}
            {spot != null && (
                <Chip
                    label={`Spot  ${fmtLTP(spot)}`}
                    size='small'
                    variant='outlined'
                    color='primary'
                    sx={{ fontWeight: 700, fontFamily: '"Roboto Mono", monospace' }}
                />
            )}

            {pcr != null && (
                <Tooltip title='Put-Call Ratio = Total PE OI / Total CE OI'>
                    <Chip
                        label={`PCR  ${pcr.toFixed(2)}`}
                        size='small'
                        variant='outlined'
                        color={pcr > 1.2 ? 'success' : pcr < 0.8 ? 'error' : 'default'}
                        sx={{ fontWeight: 700, fontFamily: '"Roboto Mono", monospace' }}
                    />
                </Tooltip>
            )}

            {/* Freshness indicator */}
            <Tooltip title={stale ? 'Engine data is stale — Greeks engine may be down' : 'Live data'}>
                <Chip
                    icon={
                        <FiberManualRecordIcon
                            sx={{ fontSize: '0.65rem !important', color: stale ? 'error.main' : 'success.main' }}
                        />
                    }
                    label={relativeTime(refreshedAt)}
                    size='small'
                    variant='outlined'
                    color={stale ? 'error' : 'success'}
                    sx={{ fontSize: '0.72rem' }}
                />
            </Tooltip>
        </Stack>
    );
}

function SubscriptionControls({ underlying, onExpiry }) {
    const queryClient = useQueryClient();
    const { data, isLoading } = useSubscriptionConfig(underlying);
    const [strikeRange, setStrikeRange] = useState(20);
    const [optionExpiry, setOptionExpiry] = useState('Auto');
    const [futureExpiry, setFutureExpiry] = useState('Auto');

    useEffect(() => {
        if (!data?.subscription) return;
        setStrikeRange(data.subscription.strikeRange ?? 20);
        setOptionExpiry(data.subscription.optionExpiry ?? 'Auto');
        setFutureExpiry(data.subscription.futureExpiry ?? 'Auto');
    }, [data]);

    const invalidateSubscriptionState = () => {
        queryClient.invalidateQueries({ queryKey: ['ocSubscription', underlying] });
        queryClient.invalidateQueries({ queryKey: ['ocExpiries', underlying] });
        queryClient.invalidateQueries({ queryKey: ['optionChain', underlying] });
        queryClient.invalidateQueries({ queryKey: ['ocUnderlyings'] });
    };

    const applyMutation = useMutation({
        mutationFn: () => saveSubscriptionConfig(underlying, {
            strikeRange,
            optionExpiry: optionExpiry === 'Auto' ? null : optionExpiry,
            futureExpiry: futureExpiry === 'Auto' ? null : futureExpiry,
            includeSpot: true,
            includeFutures: true,
            enabled: true,
        }),
        onSuccess: (result) => {
            invalidateSubscriptionState();
            const nextExpiry = result?.subscription?.optionExpiry;
            if (nextExpiry) {
                onExpiry(nextExpiry);
            }
        },
    });

    const resetMutation = useMutation({
        mutationFn: () => resetSubscriptionConfig(underlying),
        onSuccess: (result) => {
            invalidateSubscriptionState();
            const nextExpiry = result?.subscription?.optionExpiry;
            setStrikeRange(result?.subscription?.strikeRange ?? 20);
            setOptionExpiry(nextExpiry ?? 'Auto');
            setFutureExpiry(result?.subscription?.futureExpiry ?? 'Auto');
            if (nextExpiry) {
                onExpiry(nextExpiry);
            }
        },
    });

    if (!underlying) return null;

    const optionExpiryOptions = ['Auto', ...(data?.optionExpiries ?? [])];
    const futureExpiryOptions = ['Auto', ...(data?.futureExpiries ?? [])];
    const busy = applyMutation.isPending || resetMutation.isPending;
    const error = applyMutation.error || resetMutation.error;
    const successMessage = applyMutation.isSuccess
        ? 'Subscription override applied.'
        : resetMutation.isSuccess
            ? 'Subscription reset to defaults.'
            : null;

    return (
        <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.25}
            alignItems={{ xs: 'stretch', md: 'center' }}
            sx={{
                mb: 1.5,
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: (t) => alpha(t.palette.background.paper, 0.7),
            }}
        >
            <Typography
                variant='caption'
                sx={{ minWidth: 120, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}
            >
                Live Subscription
            </Typography>

            <TextField
                label='Strike Range'
                type='number'
                size='small'
                value={strikeRange}
                onChange={(event) => setStrikeRange(Number(event.target.value || 20))}
                inputProps={{ min: 1, max: 100, step: 1 }}
                sx={{ width: { xs: '100%', sm: 130 } }}
            />

            <Autocomplete
                options={optionExpiryOptions}
                value={optionExpiry}
                onChange={(_, value) => setOptionExpiry(value || 'Auto')}
                renderInput={(params) => (
                    <TextField {...params} label='Options Expiry' size='small' />
                )}
                sx={{ minWidth: 170 }}
                disableClearable
                size='small'
            />

            <Autocomplete
                options={futureExpiryOptions}
                value={futureExpiry}
                onChange={(_, value) => setFutureExpiry(value || 'Auto')}
                renderInput={(params) => (
                    <TextField {...params} label='Futures Expiry' size='small' />
                )}
                sx={{ minWidth: 170 }}
                disableClearable
                size='small'
            />

            <Button
                variant='contained'
                onClick={() => applyMutation.mutate()}
                disabled={busy || isLoading}
                sx={{ minWidth: 110 }}
            >
                {applyMutation.isPending ? 'Applying' : 'Apply'}
            </Button>

            <Button
                variant='outlined'
                onClick={() => resetMutation.mutate()}
                disabled={busy || isLoading}
                sx={{ minWidth: 110 }}
            >
                {resetMutation.isPending ? 'Resetting' : 'Default'}
            </Button>

            <Box sx={{ flexGrow: 1 }} />

            {isLoading && (
                <Typography variant='caption' color='text.secondary'>
                    Loading subscription settings...
                </Typography>
            )}

            {successMessage && !busy && (
                <Typography variant='caption' color='success.main' sx={{ fontWeight: 700 }}>
                    {successMessage}
                </Typography>
            )}

            {error && (
                <Typography variant='caption' color='error.main' sx={{ maxWidth: 320 }}>
                    {error?.response?.data?.message ?? error?.message ?? 'Failed to update subscription.'}
                </Typography>
            )}
        </Stack>
    );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function OptionChainPage() {
    const [underlying, setUnderlying] = useState(null);
    const [expiry, setExpiry]         = useState(null);
    const [viewMode, setViewMode]     = useState('data');   // 'data' | 'greeks'

    const setActive = useOptionChainStore(s => s.setActive);

    // Populate underlying list
    const { data: underlyings = [], isLoading: ulLoading } = useUnderlyings();

    // Auto-select first underlying
    useEffect(() => {
        if (underlyings.length && !underlying) {
            setUnderlying(underlyings[0]);
        }
    }, [underlyings, underlying]);

    // Populate expiry list when underlying changes
    const { data: expiries = [] } = useExpiries(underlying);

    // Auto-select nearest expiry
    useEffect(() => {
        if (expiries.length) setExpiry(expiries[0]);
    }, [expiries]);

    // Sync Zustand store
    useEffect(() => {
        if (underlying && expiry) setActive(underlying, expiry);
    }, [underlying, expiry, setActive]);

    // Chain data
    const { chain, spot, stale, refreshedAt, pcr, isLoading, isError, error } =
        useOptionChain(underlying, expiry);

    if (ulLoading) {
        return (
            <Box sx={{ p: 2 }}>
                <Skeleton variant='rectangular' height={44} sx={{ mb: 2, borderRadius: 1 }} />
                <Skeleton variant='rectangular' height='60vh' sx={{ borderRadius: 1 }} />
            </Box>
        );
    }

    if (!underlyings.length) {
        return (
            <Alert severity='warning' sx={{ m: 2 }}>
                No active underlyings found. Make sure the Greeks engine is running
                and has written snapshots to Redis.
            </Alert>
        );
    }

    return (
        <Box
            sx={{
                display:       'flex',
                flexDirection: 'column',
                height:        '100%',
                pb:            '80px',   // room for StrategyBuilderFooter
            }}
        >
            {/* Toolbar */}
            <Toolbar
                underlying={underlying}
                onUnderlying={(v) => { setUnderlying(v); setExpiry(null); }}
                expiry={expiry}
                onExpiry={setExpiry}
                viewMode={viewMode}
                onViewMode={setViewMode}
                underlyings={underlyings}
                expiries={expiries}
                spot={spot}
                stale={stale}
                refreshedAt={refreshedAt}
                pcr={pcr}
            />

            <SubscriptionControls
                underlying={underlying}
                onExpiry={setExpiry}
            />

            <Divider sx={{ mb: 1 }} />

            {/* Error state */}
            {isError && (
                <Alert severity='error' sx={{ mb: 1 }}>
                    {error?.response?.data?.message ?? error?.message ?? 'Failed to load chain data'}
                </Alert>
            )}

            {/* Table — fills remaining height */}
            <Box
                sx={{
                    flexGrow:   1,
                    overflow:   'hidden',
                    borderRadius: 1,
                    border:     '1px solid',
                    borderColor: 'divider',
                    // Horizontal scroll on mobile for full column visibility
                    overflowX:  { xs: 'auto', sm: 'hidden' },
                    bgcolor:    (t) => alpha(t.palette.background.paper, 0.6),
                }}
            >
                <OptionChainTable
                    chain={chain}
                    viewMode={viewMode}
                    isLoading={isLoading && !chain.length}
                />
            </Box>

            {/* Slide-up strategy builder */}
            <StrategyBuilderFooter />
        </Box>
    );
}
